import React, { useContext, useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { BusinessAutomationActionTypeEnum, TimezoneEnum } from '@reach/shared-types/business-automation';

const { WAIT_UNTIL_NEXT_OCCURRENCE, WAIT_UNTIL_AT_LEAST_NEXT_AVAILABLE_TIME } =
  BusinessAutomationActionTypeEnum;

type WaitUntilActionType =
  | typeof WAIT_UNTIL_AT_LEAST_NEXT_AVAILABLE_TIME
  | typeof WAIT_UNTIL_NEXT_OCCURRENCE;

import type { AutomationTemplateContents } from '@reach/shared-types/automation-template';
import { AutomationEditorContext } from '../AutomationEditorContext';
import { DialogBackdrop } from '../../../../components/ui/dialog-backdrop';
import { useToast } from '../../../../hooks/useToast';
import { StepDialogFooter } from '../StepDialogFooter';
import { TimezoneDropdown } from './TimeZoneDropdown';
import { BehaviorSelector } from './BehaviorSelector';
import styles from './WaitUntilDialog.module.css';

const { dialog, waitUntilForm } = styles;

type WaitUntilAction = Extract<
  AutomationTemplateContents['actionData'][0],
  { type: WaitUntilActionType }
>;

interface WaitUntilDialogProps {
  open: boolean;
  onClose: () => void;
  action: WaitUntilAction;
  actionIndex: number;
}

export const WaitUntilDialog: React.FC<WaitUntilDialogProps> = ({
  open,
  onClose,
  action,
  actionIndex,
}) => {
  const { editor } = useContext(AutomationEditorContext)!;
  const { updateAction } = editor;
  const { addToast } = useToast();

  const { type: actionType, timezone: actionTimezone } = action;
  const startTimezone = actionTimezone || TimezoneEnum.EST;

  const [behavior, setBehavior] = useState<WaitUntilActionType>(actionType);
  const [timeValue, setTimeValue] = useState<string>('');
  const [timezone, setTimezone] = useState<TimezoneEnum>(startTimezone);

  const formatTimeForInput = (hour: number, minute: number): string => {
    if (hour === undefined || minute === undefined) return '';
    const date = new Date();
    date.setHours(hour, minute, 0, 0);
    return new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }).format(date);
  };

  const parseTimeFromInput = (timeStr: string): { hour: number; minute: number } | null => {
    if (!timeStr) return null;
    const dateStr = `2000-01-01 ${timeStr}`;
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return null;

    return {
      hour: date.getHours(),
      minute: date.getMinutes(),
    };
  };

  useEffect(() => {
    if (open) {
      setBehavior(action.type);
      const initialHour = action.hour !== undefined ? action.hour : 9;
      const initialMinute = action.minute !== undefined ? action.minute : 0;
      const initialTime = formatTimeForInput(initialHour, initialMinute);
      setTimeValue(initialTime);
      setTimezone(actionTimezone || TimezoneEnum.EST);
    }
  }, [open, action, actionTimezone]);

  if (!open) return null;

  const handleSave = () => {
    const parsedTime = parseTimeFromInput(timeValue);
    if (!parsedTime) {
      addToast({
        variant: 'destructive',
        title: 'Invalid time',
        description: 'Please enter a valid time in HH:MM AM/PM format.',
      });
      return;
    }

    updateAction(actionIndex, {
      type: behavior,
      hour: parsedTime.hour,
      minute: parsedTime.minute,
      timezone,
    });
    onClose();
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTimeValue(e.target.value);
  };

  return (
    <>
      <DialogBackdrop onClose={onClose} />
      <dialog className={dialog} open>
        <header>
          <div>
            <h2>Configure Wait Until</h2>
            <p>Wait until the next available time</p>
          </div>
          <button onClick={onClose}>
            <X />
          </button>
        </header>

        <main>
          <form className={waitUntilForm}>
            <fieldset>
              <legend>Target Time</legend>
              <p>Specify the time to wait until.</p>
              <input
                id="target-time"
                type="text"
                value={timeValue}
                onChange={handleTimeChange}
                placeholder="--:-- --"
              />
            </fieldset>

            <fieldset>
              <legend>Timezone</legend>
              <p>Select the timezone for the target time.</p>
              <TimezoneDropdown value={timezone} onChange={setTimezone} />
            </fieldset>

            <BehaviorSelector value={behavior} onChange={setBehavior} />
          </form>
        </main>

        <StepDialogFooter onCancel={onClose} onOK={handleSave} />
      </dialog>
    </>
  );
};
